import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { useSettingsStore, type SettingsPatch } from '../stores/settings';
import { useCircleStore } from '../stores/circle';
import { api } from '../lib/api';
import { subscribeToPush, unsubscribeFromPush, isIOS } from '../hooks/usePush';
import { isStandalone } from '../lib/pwa';
import { toast } from '../components/ui/Toast';
import { IOSPushModal } from '../components/settings/IOSPushModal';
import { SectionTitle, Row, Group, Toggle, Pill } from '../components/ui/SettingsPrimitives';
import { formatError } from '../utils/formatError';
import type { NotificationSettings, QuietHoursSettings } from '@nudge/shared';

// ─── Helpers shared across sub-components ─────────────────────────────────────

type PatchFn = (patch: SettingsPatch) => void;
type PatchNotifFn = (patch: Partial<NotificationSettings>) => void;
type PatchQuietFn = (patch: Partial<QuietHoursSettings> | null) => void;

// ─── Sub-components ───────────────────────────────────────────────────────────

interface NotifProps {
  patch: PatchNotifFn;
  patchQuiet: PatchQuietFn;
  onTogglePush: (v: boolean) => void;
  busyNotif: boolean;
}

function NotificationSettings({ patch, patchQuiet, onTogglePush, busyNotif }: NotifProps) {
  const s = useSettingsStore((st) => st.settings);
  return (
    <>
      <SectionTitle>Notifications</SectionTitle>
      <Group>
        <Row label="Push notifications" hint={isIOS() ? 'iOS: requires installing as PWA' : 'Receive cards while app is closed'}>
          <Toggle ariaLabel="Push notifications" checked={s.notifications.push_enabled} onChange={onTogglePush} />
        </Row>
        {busyNotif && <Row label=""><span className="text-xs text-text-muted">Working…</span></Row>}
        <Row label="Soft cards">
          <Pill
            value={s.notifications.soft_behaviour}
            options={[{ value: 'badge', label: 'Badge' }, { value: 'visible', label: 'Visible' }, { value: 'off', label: 'Off' }]}
            onChange={(v) => patch({ soft_behaviour: v })}
          />
        </Row>
        <Row label="Firm cards">
          <Pill
            value={s.notifications.firm_behaviour}
            options={[{ value: 'visible_sound', label: 'Sound' }, { value: 'visible_silent', label: 'Silent' }, { value: 'badge', label: 'Badge' }, { value: 'off', label: 'Off' }]}
            onChange={(v) => patch({ firm_behaviour: v })}
          />
        </Row>
        <Row label="Re-nudge firm cards" hint="Sends a follow-up if not acknowledged">
          <Toggle ariaLabel="Re-nudge" checked={s.notifications.re_nudge_enabled} onChange={(v) => patch({ re_nudge_enabled: v })} />
        </Row>
        <Row label="Re-nudge delay">
          <Pill
            value={String(s.notifications.re_nudge_delay_minutes) as '10' | '15' | '30' | '60'}
            options={[{ value: '10', label: '10m' }, { value: '15', label: '15m' }, { value: '30', label: '30m' }, { value: '60', label: '1h' }]}
            onChange={(v) => patch({ re_nudge_delay_minutes: Number(v) as 10 | 15 | 30 | 60 })}
          />
        </Row>
        <Row label="Re-nudge max">
          <Pill
            value={String(s.notifications.re_nudge_max) as '0' | '1' | '2' | '3'}
            options={[{ value: '0', label: '0' }, { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }]}
            onChange={(v) => patch({ re_nudge_max: Number(v) as 0 | 1 | 2 | 3 })}
          />
        </Row>
        <Row label="Quiet hours" hint={s.quiet_hours?.enabled ? `${s.quiet_hours.start} – ${s.quiet_hours.end}` : 'Off'}>
          <Toggle
            ariaLabel="Quiet hours"
            checked={!!s.quiet_hours?.enabled}
            onChange={(v) => (v ? patchQuiet({ enabled: true }) : patchQuiet(null))}
          />
        </Row>
        {s.quiet_hours?.enabled && (
          <Row label="Window">
            <div className="flex items-center gap-2">
              <input type="time" value={s.quiet_hours.start} onChange={(e) => patchQuiet({ start: e.target.value })}
                className="rounded-lg border border-border-subtle bg-surface px-2 py-1 text-xs text-text-primary" />
              <span className="text-xs text-text-muted">to</span>
              <input type="time" value={s.quiet_hours.end} onChange={(e) => patchQuiet({ end: e.target.value })}
                className="rounded-lg border border-border-subtle bg-surface px-2 py-1 text-xs text-text-primary" />
            </div>
          </Row>
        )}
      </Group>
    </>
  );
}

function AppearanceSettings({ patch }: { patch: PatchFn }) {
  const s = useSettingsStore((st) => st.settings);
  return (
    <>
      <SectionTitle>Appearance</SectionTitle>
      <Group>
        <Row label="Theme">
          <Pill value={s.theme}
            options={[{ value: 'dark', label: 'Dark' }, { value: 'light', label: 'Light' }, { value: 'system', label: 'System' }]}
            onChange={(v) => patch({ theme: v })} />
        </Row>
        <Row label="Reduce motion">
          <Toggle ariaLabel="Reduce motion" checked={s.reduce_motion} onChange={(v) => patch({ reduce_motion: v })} />
        </Row>
        <Row label="High contrast">
          <Toggle ariaLabel="High contrast" checked={s.high_contrast} onChange={(v) => patch({ high_contrast: v })} />
        </Row>
        <Row label="Font size">
          <Pill value={s.font_size}
            options={[{ value: 'default', label: 'A' }, { value: 'large', label: 'A+' }, { value: 'xlarge', label: 'A++' }]}
            onChange={(v) => patch({ font_size: v })} />
        </Row>
        <Row label="Card size">
          <Pill value={s.card_size}
            options={[{ value: 'compact', label: 'Compact' }, { value: 'comfortable', label: 'Comfy' }, { value: 'large', label: 'Large' }]}
            onChange={(v) => patch({ card_size: v })} />
        </Row>
      </Group>
    </>
  );
}

function AccessibilitySettings({ patch }: { patch: PatchFn }) {
  const s = useSettingsStore((st) => st.settings);
  return (
    <>
      <SectionTitle>Accessibility</SectionTitle>
      <Group>
        <Row label="Haptic feedback" hint="Subtle vibrations on send/receive">
          <Toggle ariaLabel="Haptics" checked={s.haptic_feedback} onChange={(v) => patch({ haptic_feedback: v })} />
        </Row>
        <Row label="Notification sounds" hint="Plays a soft tone for incoming cards">
          <Toggle ariaLabel="Sounds" checked={s.sound_enabled} onChange={(v) => patch({ sound_enabled: v })} />
        </Row>
      </Group>
    </>
  );
}

interface AccountProps {
  onSaveName: (v: string) => Promise<void>;
  onExport: () => Promise<void>;
  onDelete: () => Promise<void>;
}

function AccountSettings({ onSaveName, onExport, onDelete }: AccountProps) {
  const user = useAuthStore((s) => s.user);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user?.display_name ?? '');

  if (!user) return null;

  async function save() {
    await onSaveName(draft);
    setEditing(false);
  }

  return (
    <>
      <SectionTitle>Account</SectionTitle>
      <Group>
        <Row label="Display name" hint={user.display_name}>
          {editing ? (
            <div className="flex items-center gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)}
                className="w-28 rounded-lg border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary" />
              <button onClick={save} className="text-xs text-sage">Save</button>
            </div>
          ) : (
            <button onClick={() => { setDraft(user.display_name); setEditing(true); }} className="text-xs text-sage">Edit</button>
          )}
        </Row>
        <Row label="Link Google" hint="Sign in on other devices">
          <button onClick={() => toast('Set VITE_GOOGLE_CLIENT_ID before deploy', 'info')}
            className="rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-xs text-text-muted">Link</button>
        </Row>
        <Row label="Link Apple" hint="Sign in on other devices">
          <button onClick={() => toast('Set VITE_APPLE_CLIENT_ID before deploy', 'info')}
            className="rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-xs text-text-muted">Link</button>
        </Row>
        <Row label="Export your data" hint="Download a JSON file">
          <button onClick={onExport} className="rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-xs text-text-primary">Export</button>
        </Row>
        <Row label="Delete account" hint="Permanently removes all your data">
          <button onClick={onDelete} className="rounded-lg border border-rose/40 bg-rose/10 px-3 py-1.5 text-xs text-rose">Delete</button>
        </Row>
      </Group>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function Settings() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const settings = useSettingsStore((s) => s.settings);
  const update = useSettingsStore((s) => s.update);
  const leaveCircle = useCircleStore((s) => s.leaveCircle);
  const navigate = useNavigate();
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [busyNotif, setBusyNotif] = useState(false);

  if (!user) return null;

  const patch: PatchFn = (p) => void update(p);
  const patchNotif: PatchNotifFn = (p) => void update({ notifications: { ...settings.notifications, ...p } });
  const patchQuiet: PatchQuietFn = (p) =>
    p === null ? void update({ quiet_hours: null })
      : void update({ quiet_hours: { enabled: true, start: '22:00', end: '07:00', ...settings.quiet_hours, ...p } });

  async function togglePush(next: boolean) {
    setBusyNotif(true);
    try {
      if (next) {
        if (isIOS() && !isStandalone()) { setIosModalOpen(true); return; }
        const res = await subscribeToPush();
        if (!res.ok) toast(`Couldn't enable push: ${res.reason}`, 'error');
        else toast('Push enabled', 'success');
      } else {
        await unsubscribeFromPush();
        toast('Push disabled', 'info');
      }
    } finally {
      setBusyNotif(false);
    }
  }

  async function saveName(v: string) {
    const trimmed = v.trim();
    if (!trimmed) return;
    try {
      const { user: updated } = await api.updateMe({ display_name: trimmed });
      setUser(updated);
      toast('Name updated', 'success');
    } catch (e) {
      toast(formatError(e, 'Could not update name'), 'error');
    }
  }

  async function exportData() {
    try {
      const blob = await api.exportMe();
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: `nudge-export-${new Date().toISOString().slice(0, 10)}.json`,
      });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast(formatError(e, 'Could not export'), 'error');
    }
  }

  async function deleteAccount() {
    if (!window.confirm('Delete your account? This removes all your data and cannot be undone.')) return;
    if (!window.confirm('Are you sure? Last chance.')) return;
    try {
      await api.deleteMe();
      try { await leaveCircle(); } catch { /* ignore if already left */ }
      await logout();
      navigate('/welcome', { replace: true });
    } catch (e) {
      toast(formatError(e, 'Could not delete account'), 'error');
    }
  }

  return (
    <div className="pb-8">
      <NotificationSettings patch={patchNotif} patchQuiet={patchQuiet} onTogglePush={togglePush} busyNotif={busyNotif} />
      <AppearanceSettings patch={patch} />
      <AccessibilitySettings patch={patch} />
      <AccountSettings onSaveName={saveName} onExport={exportData} onDelete={deleteAccount} />
      <IOSPushModal open={iosModalOpen} onClose={() => setIosModalOpen(false)} />
    </div>
  );
}
